/**
 * @module confirmSyncQB/columnSort
 * @description 场景 2：字段列排序（panel → QB）— Confirm 阶段同步
 *
 * ## 背景
 * 字段列排序在面板侧维护 `column_sort`（展示顺序 + 虚拟列如 %、对照组），
 * QB 侧维护 `columnDimensions` / `columnMetrics`（物理字段槽位）。
 * 用户拖拽只改 draft；点击 Confirm 后需把顺序写回 QB，并通知图表重排列。
 *
 * ## 数据流（方向② panel → QB）
 * ```
 * FieldColumnSorting 拖拽
 *   → mapQuickDragItemsToColumnSort
 *   → UPDATE_DRAFT { column_sort }
 * useSettingsDraft.onConfirm
 *   → confirmSyncToQB
 *   → buildColumnSortSyncPatch（本模块）
 *   → 单次 DVA save
 * ```
 *
 * ## 与 handlers/columnSort 的分工
 * - `linkage/qbFields/qbLinkage/handlers/columnSort/*`：QB↔面板推导、联动 sanitize、增删改 column_sort 项
 * - `confirmSyncQB/columnSort/*`：仅 Confirm 时把 panel 顺序落到 QB + 刷新 columnSortToken
 *
 * @see confirmSyncQB/index.ts — 方向② 唯一入口（仅 column_sort patch）
 * @see AnalysisLineChart — 监听 columnSortToken 调用 applyColumnOrder
 */

import type { IColumnSortItem } from '@/pages/datalab/pages/Multidimensional/Analysis/types/chartSettings/fieldColumnSort';
import type { IConfirmSyncToQBParams } from '../types';
import { sortFieldsByColumnSort } from './sortFields';

/**
 * 根据 Confirm 后的 `column_sort` 生成写回 QB 的 DVA patch。
 *
 * **何时有输出**：`commitBase.column_sort` 为非空数组（与 `hasPersistedColumnSort` 一致，
 * 表示用户已保存过列排序配置，而非仅由 QB 临时推导的展示列表）。
 *
 * **返回字段**：
 * - `columnDimensions` / `columnMetrics`：按 column_sort 重排后的 QB 槽位顺序
 * - `columnSortToken`：带时间戳，供 `AnalysisLineChart` 触发 `applyColumnOrder`
 *   （表格列视觉顺序与 QB 物理顺序解耦，需显式 token 驱动刷新）
 *
 * @returns 无需同步时返回 `{}`，由 `confirmSyncToQB` 跳过 dispatch
 */
export function buildColumnSortSyncPatch(
  params: IConfirmSyncToQBParams,
): Record<string, unknown> {
  const { commitBase, columnDimensions, columnMetrics } = params;

  const columnSort = commitBase.column_sort;
  if (!(Array.isArray(columnSort) && columnSort.length > 0)) {
    return {};
  }

  const sort = columnSort as IColumnSortItem[];
  const { sortedColumnDimensions, sortedColumnMetrics } =
    sortFieldsByColumnSort({
      columnSort: sort,
      columnDimensions,
      columnMetrics,
    });

  return {
    columnDimensions: sortedColumnDimensions,
    columnMetrics: sortedColumnMetrics,
    columnSortToken: { columnSort: sort, ts: Date.now() },
  };
}
